import styles from './RulesDetailsList.module.scss';

import * as React from 'react';
import moment from 'moment-timezone';

import { useEffect, useMemo } from 'react';
import {
  DetailsList,
  ConstrainMode,
  IColumn,
  Stack,
  Selection,
  SelectionMode,
  Checkbox,
  CheckboxVisibility,
  Panel,
  PanelType,
  DetailsRow,
  IDetailsRowProps,
} from '@fluentui/react';
import { Badge } from '../core/Badge';
import { useQueryString } from '../../util/hooks';
import { css } from '../../util/Css';
import { RuleEdit } from './RuleEdit';
import { Rule } from '../../api/insurance.types';

export const RulesDetailsList = ({ items, refresh }: { items: Rule[]; refresh: () => void }) => {
  const [rule, setRule] = useQueryString('rule', '');

  const selection = useMemo(
    () =>
      new Selection({
        selectionMode: SelectionMode.single,
        getKey: (item: any) => item.id,
        onSelectionChanged: () => {
          if (selection.getSelectedCount() > 0 && selection.getSelection().length > 0) {
            setRule(selection.getSelection()[0].id);
          }
        },
      }),
    [setRule]
  );

  useEffect(() => {
    if (selection.getSelectedCount() === 0 && items.length > 0) {
      selection.setKeySelected(rule, true, true);
    }

    // Intentional to not have rule in dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, selection]);

  useEffect(() => {
    if (!rule && selection.getSelectedCount() > 0 && selection.getSelection().length > 0) {
      selection.setKeySelected(selection.getSelection()[0].id as string, false, false);
    }
  }, [rule, selection]);

  const columns: IColumn[] = [
    // {
    //   key: 'id',
    //   name: 'ID',
    //   fieldName: 'id',
    //   minWidth: 80,
    //   maxWidth: 120,
    // },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: Rule) => {
        return (
          <div>
            <div>{item.name}</div>
            <div>{item.notes}</div>
          </div>
        );
      },
    },
    {
      key: 'criteria',
      name: 'Criteria',
      minWidth: 140,
      isResizable: true,
      onRender: (item: Rule) => {
        return item.criteria.map((c, i) => {
          return (
            <Stack horizontal tokens={{ padding: 2, childrenGap: 2 }} key={i}>
              <Badge text={c.attribute} style={{ textTransform: 'none', backgroundColor: '#ccc', minWidth: 100 }} />
              <Badge text={c.matchType} style={{ textTransform: 'none', backgroundColor: '#ccc', minWidth: 70 }} />
              <Badge text={c.value} style={{ textTransform: 'none', backgroundColor: '#ccc' }} />
              {c.inverted && <Badge text="invert" style={{ backgroundColor: '#ccc' }} />}
            </Stack>
          );
        });
      },
    },
    {
      key: 'partitionName',
      name: 'Rule Type',
      fieldName: 'partitionName',
      minWidth: 140,
      maxWidth: 140,
    },
    {
      key: 'ruleParameters',
      name: 'Rule Parameters',
      minWidth: 160,
      isResizable: true,
      onRender: (item: Rule) => {
        return <pre>{JSON.stringify(item.ruleParameters, undefined, 2)}</pre>;
      },
    },
    {
      key: 'beginDate',
      name: 'Begin',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: Rule) => moment(item.beginDate).format('MM/DD/YYYY'),
    },
    {
      key: 'endDate',
      name: 'End',
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: Rule) => moment(item.endDate).format('MM/DD/YYYY'),
    },
    {
      key: 'active',
      name: 'Active',
      fieldName: 'active',
      minWidth: 60,
      maxWidth: 60,
      onRender: (item: Rule) => <Checkbox checked={item.active} disabled={true} />,
    },
  ];

  const selectedRule = items.find((r) => r.id === rule);

  const onRenderRow = (props?: IDetailsRowProps): JSX.Element => {
    const item = props!.item as Rule;
    const rowClass = css({
      [styles.ruleInactive]: !item.active,
      [styles.ruleExpired]: moment(item.endDate).isBefore(moment()),
    });
    return <DetailsRow className={rowClass} {...props!} />;
  };

  return (
    <>
      <DetailsList
        className={styles.rulesDetailList}
        selection={selection}
        selectionPreservedOnEmptyClick={true}
        checkboxVisibility={CheckboxVisibility.hidden}
        constrainMode={ConstrainMode.horizontalConstrained}
        items={items}
        setKey="set"
        columns={columns}
        onRenderRow={onRenderRow}
        onShouldVirtualize={() => false}
      />
      {selectedRule && (
        <Panel isBlocking={false} isOpen={true} onDismiss={() => setRule('')} type={PanelType.largeFixed} headerText={`Rule ${selectedRule.name}`}>
          <RuleEdit
            original={selectedRule}
            onDone={() => {
              setRule('');
              refresh();
            }}
          />
        </Panel>
      )}
    </>
  );
};
